import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
  activateCustomDashboardRevision,
  getCustomDashboardRevisionData,
  getCustomDashboardRevisionHtml,
  isRevisionConflict,
} from '@/lib/api/custom-dashboard';
import { CustomDashboardViewer } from './CustomDashboardViewer';

export interface PreviewDialogProps {
  accountId: string;
  authHeaders: Record<string, string>;
  tabId: string;
  tabName: string;
  /** タブの現在の公開 revision（未公開なら null）。activate の楽観ロックに使う。 */
  activeRevision: number | null;
  /** プレビュー対象の revision。 */
  revision: number;
  onClose: () => void;
}

/** 指定 revision を表示と同じ wrapper（CSP + sandbox iframe + データ注入）で描画するダイアログ。 */
export function PreviewDialog({
  accountId,
  authHeaders,
  tabId,
  tabName,
  activeRevision,
  revision,
  onClose,
}: PreviewDialogProps) {
  const queryClient = useQueryClient();
  const [isNavigationBlocked, setIsNavigationBlocked] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  // Viewer の useEffect が onNavigation を deps に持つため、参照を安定させ iframe の再生成を防ぐ。
  const handleNavigation = useCallback(() => setIsNavigationBlocked(true), []);

  // 表示経路（CustomDashboardTabViewers）と同じ queryKey + staleTime でキャッシュを共有する。
  const htmlQuery = useQuery({
    queryKey: ['custom-dashboard-revision-html', accountId, tabId, revision],
    queryFn: ({ signal }) =>
      getCustomDashboardRevisionHtml(accountId, tabId, revision, authHeaders, signal),
    staleTime: Infinity,
  });
  const dataQuery = useQuery({
    queryKey: ['custom-dashboard-revision-data', accountId, tabId, revision],
    queryFn: ({ signal }) =>
      getCustomDashboardRevisionData(accountId, tabId, revision, authHeaders, signal),
    staleTime: Infinity,
  });
  const isLoaded = htmlQuery.data !== undefined && dataQuery.data !== undefined;
  const loadError = htmlQuery.error ?? dataQuery.error;

  const isActive = revision === activeRevision;
  const isRollback = activeRevision !== null && revision < activeRevision;

  // 公開は表示（activeRevision）を変えるため、設定画面（all-tabs）と表示経路（tabs）の両方を
  // invalidate する。
  const invalidateTabQueries = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['custom-dashboard-all-tabs', accountId] }),
      queryClient.invalidateQueries({ queryKey: ['custom-dashboard-tabs', accountId] }),
    ]);

  const handleActivate = async () => {
    setIsActivating(true);
    try {
      await activateCustomDashboardRevision(
        accountId,
        tabId,
        { expectedActiveRevision: activeRevision, revision },
        authHeaders,
      );
      await invalidateTabQueries();
      toast.success(`revision ${revision} を公開しました`);
      onClose();
    } catch (error) {
      if (isRevisionConflict(error)) {
        await invalidateTabQueries();
        toast.error('他のユーザーが公開状態を変更したため最新の状態を読み込み直しました');
        // このダイアログの activeRevision は開いた時点の値のため、閉じて最新状態から開き直してもらう。
        onClose();
      } else {
        toast.error(error instanceof Error ? error.message : '公開に失敗しました');
      }
    } finally {
      setIsActivating(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !isActivating) onClose();
      }}
    >
      <DialogContent className="flex h-[85vh] max-w-6xl flex-col">
        <DialogHeader>
          <DialogTitle>
            {tabName} r{revision} をプレビュー中
          </DialogTitle>
          <DialogDescription>
            本番と同じ wrapper（CSP + sandbox iframe）で描画しています
          </DialogDescription>
          <div className="flex items-center gap-2">
            {isActive ? (
              <Badge>この revision は公開中</Badge>
            ) : (
              <Badge variant="outline">未公開</Badge>
            )}
          </div>
        </DialogHeader>
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border">
          {isNavigationBlocked ? (
            <Alert variant="destructive" className="m-4 max-w-xl self-center">
              <AlertTitle>プレビューを停止しました</AlertTitle>
              <AlertDescription>
                ダッシュボード内の画面遷移を検知したため表示を停止しました
              </AlertDescription>
            </Alert>
          ) : htmlQuery.data !== undefined && dataQuery.data !== undefined ? (
            <CustomDashboardViewer
              html={htmlQuery.data}
              dataBuffer={dataQuery.data}
              onNavigation={handleNavigation}
            />
          ) : htmlQuery.isFetching || dataQuery.isFetching ? (
            <Skeleton aria-label="読み込み中" className="h-full w-full" />
          ) : loadError != null ? (
            <div className="flex h-full w-full items-center justify-center p-6">
              <Alert variant="destructive" className="max-w-xl">
                <AlertTitle>プレビューを取得できませんでした</AlertTitle>
                <AlertDescription className="space-y-4">
                  <p>{loadError instanceof Error ? loadError.message : String(loadError)}</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void Promise.all([htmlQuery.refetch(), dataQuery.refetch()])}
                  >
                    再試行
                  </Button>
                </AlertDescription>
              </Alert>
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isActivating}>
            閉じる
          </Button>
          {!isActive && (
            <Button
              onClick={() => void handleActivate()}
              disabled={isActivating || !isLoaded || isNavigationBlocked}
            >
              {isActivating
                ? '公開中...'
                : isRollback
                  ? 'この revision に戻す（ロールバック）'
                  : 'この revision を公開'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
